(() => {
  // ---------- Small helpers for custom font loading ----------
  const FONT_REG = (window.__engrFonts ||= {
    byUrl: new Map(),         // url -> family
    cssEl: null
  });

  function ensureCssEl() {
    if (!FONT_REG.cssEl) {
      FONT_REG.cssEl = document.createElement('style');
      FONT_REG.cssEl.setAttribute('data-engr-fonts', 'true');
      document.head.appendChild(FONT_REG.cssEl);
    }
    return FONT_REG.cssEl;
  }

  function extToFormat(url) {
    const m = (url || '').split('?')[0].toLowerCase();
    if (m.endsWith('.woff2')) return 'woff2';
    if (m.endsWith('.woff'))  return 'woff';
    if (m.endsWith('.ttf'))   return 'truetype';
    if (m.endsWith('.otf'))   return 'opentype';
    if (m.endsWith('.eot'))   return 'embedded-opentype';
    if (m.endsWith('.svg'))   return 'svg';
    return 'truetype';
  }

  function hash32(str) {
    let h = 2166136261 >>> 0;
    for (let i = 0; i < str.length; i++) {
      h ^= str.charCodeAt(i);
      h = Math.imul(h, 16777619);
    }
    return (h >>> 0).toString(36);
  }

  /**
   * Register a font-face for a given file URL once; return a stable family name.
   * No fallbacks: caller can set font-family to this family alone.
   */
  function registerFontFromUrl(url, weight = '400', style = 'normal') {
    const clean = (url || '').trim();
    if (!clean) return '';
    if (FONT_REG.byUrl.has(clean)) return FONT_REG.byUrl.get(clean);

    const family = `engr-font-${hash32(clean).slice(0, 8)}`;
    const fmt = extToFormat(clean);
    const css = `
@font-face{
  font-family:'${family}';
  src:url('${clean}') format('${fmt}');
  font-weight:${weight};
  font-style:${style};
  font-display:swap;
}`;
    ensureCssEl().appendChild(document.createTextNode(css));
    FONT_REG.byUrl.set(clean, family);
    return family;
  }
  // -----------------------------------------------------------

  if (!customElements.get('engraving-drawer-form')) {
    class EngravingDrawerForm extends HTMLElement {
      connectedCallback() {
        if (this.querySelector('engraving-drawer-form')) {
          this.setAttribute('data-engraving-skip', 'true');
          return;
        }

        const rawMin = parseInt(this.dataset.minChars || '1', 10);
        const rawMax = parseInt(this.dataset.maxChars || '3', 10);
        this.min = Number.isFinite(rawMin) && rawMin > 0 ? rawMin : 1;
        this.max = Number.isFinite(rawMax) && rawMax >= this.min ? rawMax : this.min;

        const allowedRaw = (this.dataset.allowedChars || '').toString().trim();
        this.allowedChars = allowedRaw ? Array.from(allowedRaw.toUpperCase()) : null;

        this.defaultFont   = this.dataset.fontDefault || 'Georgia Pro';
        this.defaultLocKey = (this.dataset.defaultKey || 'front').toLowerCase();

        const rawAddOnId = (this.dataset.addOnId || '').toString().trim();
        this.addOnId   = /^\d+$/.test(rawAddOnId) ? rawAddOnId : '';
        this.parentSku = this.dataset.parentSku || '';
        this.parentTitle = this.dataset.parentTitle || this.getParentTitleFallback();
        this.confirmStatement = this.dataset.confirmStatement || '';

        const bodyEl   = this.closest('.loess-quickview-cart-drawer-form__body');
        let   headerEl = bodyEl && bodyEl.previousElementSibling;
        if (!headerEl || !headerEl.classList || !headerEl.classList.contains('loess-quickview-cart-drawer-form__header')) {
          headerEl = document.querySelector('.loess-quickview-cart-drawer-form__header');
        }
        this.stage   = (headerEl && headerEl.querySelector('.engraving-stage')) || this.querySelector('.engraving-stage');
        this.baseImg = (headerEl && headerEl.querySelector('.engraving-base'))  || this.querySelector('.engraving-base');
        this.overlay = (headerEl && headerEl.querySelector('[data-engraving-overlay]')) || this.querySelector('[data-engraving-overlay]');

        this.imageNoteEls = headerEl ? Array.from(headerEl.querySelectorAll('[data-image-note]')) : [];
        this.defaultImageNote = (this.imageNoteEls[0]?.textContent || '').trim();

        this.frontSrc = (this.stage && this.stage.dataset.frontSrc) || '';
        this.frontAlt = (this.stage && this.stage.dataset.frontAlt) || 'Front';

        this.input         = this.querySelector('[data-engraving-input]');
        this.fontBtns      = Array.from(this.querySelectorAll('.engraving-font'));
        this.locsRoot      = this.querySelector('[data-engraving-locations]') || this.querySelector('.engraving-locations');
        this.allowedWrap   = this.querySelector('[data-allowed-display]') || null;
        this.allowedTextEl = this.querySelector('[data-allowed-text]') || null;

        this.consentEl  = this.querySelector('[data-engraving-consent]') || null;
        this.consentMsg = this.querySelector('.engraving-consent-error') || null;

        this.maxLabel   = this.querySelector('[data-engraving-max-label]');

        this.productFormEl   = this.querySelector('form[action*="/cart/add"]');
        this.submitBtn       = this.querySelector('[data-engraving-submit]') || this.querySelector('.product-form__buttons [type="button"]');
        this.submitLabelSpan = this.querySelector('[data-engraving-submit-label]') || (this.submitBtn ? this.submitBtn.querySelector('span') : null);

        if (this.submitBtn && this.submitBtn.classList.contains('product-form__submit')) {
          this.submitBtn.classList.remove('product-form__submit');
        }
        if (this.submitBtn && !this.submitBtn.dataset.originalText) {
          this.submitBtn.dataset.originalText = (this.submitLabelSpan && this.submitLabelSpan.textContent) || 'Add Engraved to Cart';
        }

        this.qIcon     = this.querySelector('[data-question-open],[data-question-trigger]');
        this.qWidget   = this.querySelector('[data-question-widget]');
        this.qClosers  = this.qWidget ? Array.from(this.qWidget.querySelectorAll('[data-question-close], .close_btn')) : [];
        this.qContent  = this.qWidget ? this.qWidget.querySelector('[data-question-content]') : null;

        if (this.qIcon) this.qIcon.style.cursor = 'pointer';
        if (this.qClosers && this.qClosers.length) this.qClosers.forEach(el => el.style.cursor = 'pointer');

        // Read fonts JSON (now includes optional file URL) and hydrate font buttons.
        this.fontMeta = this.readFontsJSON();
        this.hydrateFontButtonsFromMeta();

        if (this.qWidget && !this.qWidget.dataset._wired) {
          const contentSel  = '.question-widget-content';
          const backdropSel = '.question-widget-layout';

          this._modalIntercept = (ev) => {
            if (!this.qWidget.classList.contains('open')) return;

            const t         = ev.target;
            const onBackdrop= !!t.closest(backdropSel);
            const inContent = !!t.closest(contentSel);
            const onCloser  = !!t.closest('[data-question-close], .close_btn');

            if (onBackdrop || onCloser) {
              ev.preventDefault();
              ev.stopPropagation();
              ev.stopImmediatePropagation();
              this.closeQuestion();
              return;
            }
            if (inContent) {
              ev.stopPropagation();
              ev.stopImmediatePropagation();
              return;
            }
            ev.stopPropagation();
            ev.stopImmediatePropagation();
          };

          ['pointerdown','click','mousedown','touchstart'].forEach(t => {
            this.qWidget.addEventListener(t, this._modalIntercept, true);
          });

          const backdropEl = this.qWidget.querySelector(backdropSel);
          if (backdropEl) backdropEl.setAttribute('data-question-close', '');

          if (this.qClosers && this.qClosers.length) {
            this.qClosers.forEach(el => {
              el.addEventListener('click', (ev) => {
                if (!this.qWidget.classList.contains('open')) return;
                ev.preventDefault();
                ev.stopPropagation();
                ev.stopImmediatePropagation();
                this.closeQuestion();
              }, true);
            });
          }

          this.qWidget.dataset._wired = '1';
        }

        this._qParent = null;
        this._qNext   = null;
        this._qPortaled = false;

        this.drawerEl       = this.closest('loess-drawer') || this.closest('.drawer');
        this.drawerCloseBtn = (this.drawerEl && this.drawerEl.querySelector('[close]')) || null;

        this.views = this.readViewsJSON();
        if (this.locsRoot) this.hydrateOrRenderButtons();

        if (this.views.length) {
          const first = this.views.find(v => v.is_default) || this.views[0];
          this.currentViewKey = first.key;
          this.applyView(this.currentViewKey, true);
        } else {
          this.currentViewKey = this.defaultLocKey;
        }

        this.currentFont = this.defaultFont;

        // If a font button is already "active", apply it immediately (and use custom file if present).
        const activeBtn = this.fontBtns.find(b => b.classList.contains('is-active') || b.getAttribute('aria-checked') === 'true');
        if (activeBtn) {
          const label  = activeBtn.dataset.fontLabel || activeBtn.textContent.trim() || this.defaultFont;
          const stack  = activeBtn.dataset.fontStack || `'${label}'`;
          const weight = activeBtn.dataset.fontWeight || '700';
          this.applyFont(label, stack, weight);
        }

        this.updateOverlay(this.input ? this.input.value : '');
        this.setSubmitEnabled();

        this._submitting = false;

        this.bindEvents();
      }

      getParentTitleFallback() {
        const t = document.querySelector('.product__title, h1.product__title, h1[itemprop="name"], .product-title, h1');
        return t ? t.textContent.trim() : '';
      }

      blockDrawerOverlay(block) {
        const overlays = document.querySelectorAll('.drawer__overlay,[close].drawer__overlay,#CartDrawer-Overlay');
        overlays.forEach(ov => {
          if (block) {
            if (ov.dataset._engrPrevPe == null) ov.dataset._engrPrevPe = ov.style.pointerEvents || '';
            ov.style.pointerEvents = 'none';
          } else {
            ov.style.pointerEvents = ov.dataset._engrPrevPe || '';
            delete ov.dataset._engrPrevPe;
          }
        });
      }

      // === Document-capture guard while modal is open ===
      addModalDocCapture() {
        if (this._docCap) return;
        const contentSel  = '.question-widget-content';
        const backdropSel = '.question-widget-layout';

        this._docCap = (ev) => {
          if (!this.qWidget || !this.qWidget.classList.contains('open')) return;

          const t          = ev.target;
          const inContent  = !!t.closest(contentSel);
          const onBackdrop = !!t.closest(backdropSel);
          const onCloser   = !!t.closest('[data-question-close], .close_btn');
          const inWidget   = !!t.closest('question-widget');

          if (onBackdrop || onCloser) {
            ev.preventDefault();
            ev.stopImmediatePropagation();
            this.closeQuestion();
            return;
          }

          if (inWidget || true) {
            ev.preventDefault();
            ev.stopImmediatePropagation();
            return;
          }
        };

        ['pointerdown','click','mousedown','touchstart'].forEach(t =>
          document.addEventListener(t, this._docCap, true)
        );
      }

      removeModalDocCapture() {
        if (!this._docCap) return;
        ['pointerdown','click','mousedown','touchstart'].forEach(t =>
          document.removeEventListener(t, this._docCap, true)
        );
        this._docCap = null;
      }
      // ===================================================

      portalQuestionWidget(toBody) {
        if (!this.qWidget) return;
        if (toBody && !this._qPortaled) {
          this._qParent = this.qWidget.parentNode;
          this._qNext   = this.qWidget.nextSibling;
          document.body.appendChild(this.qWidget);
          this._qPortaled = true;
        } else if (!toBody && this._qPortaled) {
          if (this._qParent) {
            if (this._qNext && this._qNext.parentNode === this._qParent) {
              this._qParent.insertBefore(this.qWidget, this._qNext);
            } else {
              this._qParent.appendChild(this.qWidget);
            }
          }
          this._qPortaled = false;
        }
      }

      readViewsJSON() {
        try {
          const tag = this.querySelector('script[data-engraving-views]') ||
                      document.querySelector('script[data-engraving-views]');
          if (!tag) return [];
          const raw = (tag.textContent || '').trim();
          if (!raw) return [];
          const arr = JSON.parse(raw);
          if (!Array.isArray(arr)) return [];

          return arr.map((v, i) => {
            const key   = (v.key || String(i)).toString();
            const label = v.label || v.button_label || v.type || key;
            const img   = v.image || null;

            let x = parseFloat(v.x);
            if (!Number.isFinite(x)) x = 50;
            x = Math.max(0, Math.min(100, x));

            let y = parseFloat(v.y);
            if (!Number.isFinite(y)) y = 23;
            y = Math.max(0, Math.min(100, y));

            let fs = parseInt((v.font_size ?? v.size_px), 10);
            if (Number.isNaN(fs)) fs = 54;

            const ls = (typeof v.letter_spacing === 'string' && v.letter_spacing.trim().length)
              ? v.letter_spacing.trim()
              : (Number.isFinite(parseFloat(v.letter_spacing)) ? `${parseFloat(v.letter_spacing)}px` : '0.08em');

            const rot = (typeof v.rotate === 'string') ? v.rotate : `${parseInt(v.rotation || 0,10)}deg`;
            const col = v.color || '#262626';
            const op  = (v.opacity === 0 || v.opacity) ? Number(v.opacity) : 1;

            const min = Number.isFinite(Number(v.min)) && Number(v.min) > 0 ? Number(v.min) : undefined;
            const max = Number.isFinite(Number(v.max)) && Number(v.max) > 0 ? Number(v.max) : undefined;
            const allowed = (v.allowed || '').toString();

            const imageNote = (v.image_note || '').toString();
            const question  = (v.question_mark_content || '').toString();

            // NEW: text alignment (left / center / right), default center
            const alignRaw = (v.align || v.text_alignment || '').toString().toLowerCase();
            let align = 'center';
            if (alignRaw === 'left' || alignRaw === 'right' || alignRaw === 'center') {
              align = alignRaw;
            }

            return {
              key, label, img, x, y,
              fontSize: fs,
              letterSpacing: ls,
              rotation: rot,
              color: col,
              opacity: op,
              min, max, allowed,
              imageNote,
              questionContent: question,
              align,
              is_default: !!v.is_default
            };
          });
        } catch (_e) {
          return [];
        }
      }

      // Read fonts JSON. Accepts: label, stack, weight, image, file (URL), image_alt, is_default
      readFontsJSON() {
        try {
          const tag = this.querySelector('script[data-engraving-fonts]') ||
                      document.querySelector('script[data-engraving-fonts]');
          if (!tag) return [];
          const raw = (tag.textContent || '').trim();
          if (!raw) return [];
          const arr = JSON.parse(raw);
          if (!Array.isArray(arr)) return [];

          return arr.map((f, i) => {
            const label  = f.label || f.name || '';
            const weight = (f.weight || f.font_weight || '700') + '';
            const stack  = f.stack || f.font_stack || '';
            const file   = f.file || f.font_file || '';
            const key    = (f.key || f.handle || label || String(i)).toString();
            const preview= f.preview_image || f.image || null;

            let imgUrl = '';
            if (typeof preview === 'string') imgUrl = preview;
            else if (preview && typeof preview === 'object') imgUrl = preview.url || preview.src || '';

            return { key, label, stack, weight, file, image: imgUrl, is_default: !!f.is_default };
          });
        } catch (_e) {
          return [];
        }
      }

      // Use meta to decorate font buttons:
      // - if file is present, register @font-face and use that family
      // - if preview image is present, show image inside the square
      // - always show the font name *below* the square, outside the button
      hydrateFontButtonsFromMeta() {
        if (!this.fontBtns || !this.fontBtns.length) return;

        const byLabel = new Map();
        const byKey   = new Map();

        if (Array.isArray(this.fontMeta) && this.fontMeta.length) {
          this.fontMeta.forEach(f => {
            if (f.label) byLabel.set(f.label.trim().toLowerCase(), f);
            if (f.key)   byKey.set(String(f.key).toLowerCase(), f);
          });
        }

        this.fontBtns.forEach((btn, idx) => {
          const curLabel = (btn.dataset.fontLabel || btn.textContent || '').trim();
          const keyAttr  = (btn.dataset.fontKey || '').trim().toLowerCase();

          let meta = null;
          if (keyAttr && byKey.has(keyAttr)) {
            meta = byKey.get(keyAttr);
          } else if (curLabel && byLabel.has(curLabel.toLowerCase())) {
            meta = byLabel.get(curLabel.toLowerCase());
          }

          if (!meta) return;

          const label  = meta.label || curLabel || 'Font';

          // --- ensure wrapper: <div class="engraving-font__item">[button][label]</div> ---
          let wrapper = btn.closest('.engraving-font__item');
          if (!wrapper || !wrapper.classList.contains('engraving-font__item')) {
            const parent = btn.parentElement;
            if (parent && parent.classList.contains('engraving-fonts')) {
              wrapper = document.createElement('div');
              wrapper.className = 'engraving-font__item';
              parent.insertBefore(wrapper, btn);
              wrapper.appendChild(btn);
            } else {
              // fallback: if structure is unexpected, keep current parent
              wrapper = parent || btn.parentElement;
            }
          }

          // If a file is provided, register the font and set the button’s stack
          if (meta.file && typeof meta.file === 'string' && meta.file.trim().length) {
            const family = registerFontFromUrl(meta.file, meta.weight || '700', 'normal');
            if (family) {
              btn.dataset.fontStack = `'${family}'`;  // no fallback
              btn.style.fontFamily  = `'${family}'`;
            }
          } else if (meta.stack && meta.stack.trim().length) {
            // No file, keep provided stack (legacy behavior)
            btn.dataset.fontStack = meta.stack;
            btn.style.fontFamily  = meta.stack;
          }

          if (meta.weight) btn.dataset.fontWeight = String(meta.weight);
          if (meta.label  && !btn.dataset.fontLabel) btn.dataset.fontLabel = meta.label;

          const url = (meta.image || '').trim();

          // Reset button contents (we only want preview inside the square)
          btn.innerHTML = '';
          btn.setAttribute('aria-label', label);

          if (url) {
            btn.classList.add('engraving-font--has-image');
            // Preview image inside the bordered square
            btn.style.removeProperty('font-family');

            const img = document.createElement('img');
            img.className = 'engraving-font__preview';
            img.alt       = label;
            img.src       = url;
            img.loading   = 'lazy';
            img.decoding  = 'async';
            btn.appendChild(img);
          } else {
            btn.classList.remove('engraving-font--has-image');
            // No image: show a simple “ABC” sample using the font
            const sample = document.createElement('span');
            sample.className = 'engraving-font__sample';
            sample.textContent = 'ABC';
            btn.appendChild(sample);
          }

          // Visible name UNDER the square, outside the button
          if (wrapper) {
            const existing = wrapper.querySelector('.engraving-font__label');
            if (existing) existing.remove();

            const nameSpan = document.createElement('span');
            nameSpan.className = 'engraving-font__label small-caption';
            nameSpan.textContent = label;
            wrapper.appendChild(nameSpan);
          }
        });
      }


      hydrateOrRenderButtons() {
        if (!this.locsRoot) return;

        const existing = Array.from(this.locsRoot.querySelectorAll('button'));
        if (existing.length) {
          existing.forEach((btn, idx) => {
            const key = btn.getAttribute('data-view-key') || btn.getAttribute('data-loc-key') || (this.views[idx] && this.views[idx].key) || String(idx);
            btn.dataset.viewKey = key;
            btn.setAttribute('data-view-key', key);
            btn.setAttribute('role', 'radio');
            if (!btn.hasAttribute('aria-checked')) {
              btn.setAttribute('aria-checked', idx === 0 ? 'true' : 'false');
            }
          });
          this.viewBtns = Array.from(this.locsRoot.querySelectorAll('[data-view-key]'));
          return;
        }

        if (!this.views.length) return;
        const frag = document.createDocumentFragment();
        this.views.forEach((v, idx) => {
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'engraving-loc layer__button small-caption';
          b.dataset.viewKey = v.key;
          b.setAttribute('role', 'radio');
          b.setAttribute('aria-checked', idx === 0 ? 'true' : 'false');
          b.textContent = v.label;
          if (idx === 0 || v.is_default) b.classList.add('card-swatches__button--active', 'is-active');
          frag.appendChild(b);
        });
        this.locsRoot.appendChild(frag);
        this.viewBtns = Array.from(this.locsRoot.querySelectorAll('[data-view-key]'));
      }

      updateLocActiveUI() {
        if (!this.viewBtns || !this.viewBtns.length) return;
        this.viewBtns.forEach((btn) => {
          const keyAttr = btn.getAttribute('data-view-key') || btn.getAttribute('data-loc-key');
          const active = keyAttr === this.currentViewKey;
          btn.classList.toggle('is-active', active);
          btn.classList.toggle('card-swatches__button--active', active);
          btn.setAttribute('aria-checked', active ? 'true' : 'false');
        });
      }

      applyFont(name, stack, weight) {
        this.currentFont = name || this.defaultFont;
        if (!this.overlay || !this.overlay.style) return;
        // Force the preview to the given stack (may be a single custom family when a file is present)
        this.overlay.style.setProperty('--engraving-weight', String(parseInt(weight || '700', 10)));
        this.overlay.style.setProperty('--engraving-font', stack || `'${this.currentFont}'`);
        this.overlay.style.setProperty('font-variation-settings', `"wght" ${parseInt(weight || '700', 10)}`);
      }

      sanitize(val) {
        const raw = (val || '').toUpperCase();
        if (this.allowedChars && this.allowedChars.length) {
          const filtered = Array.from(raw).filter(ch => this.allowedChars.includes(ch)).join('');
          return filtered.slice(0, this.max);
        }
        return raw.replace(/[^A-Z0-9]/g, '').slice(0, this.max);
      }

      updateOverlay(raw) {
        const clean = this.sanitize(raw);
        if (this.input && this.input.value !== clean) this.input.value = clean;
        if (this.overlay) this.overlay.textContent = clean || 'ABC';
        this.setSubmitEnabled();
      }

      applyView(key, fromInit = false) {
        this.currentViewKey = key || this.defaultLocKey;
        const view = this.views.find(v => v.key === this.currentViewKey) || this.views[0] || null;

        if (view && this.stage) {
          const imgSrc = view.img || this.frontSrc;
          if (imgSrc && this.baseImg) {
            this.baseImg.src = imgSrc;
            this.baseImg.alt = `${view.label || this.frontAlt}`;
          }
          if (this.overlay && this.overlay.style) {
            const px = `${parseInt(view.fontSize,10)}px`;
            this.overlay.style.setProperty('--engraving-x', `${view.x}%`);
            this.overlay.style.setProperty('--engraving-y', `${view.y}%`);
            this.overlay.style.setProperty('--engraving-size', px);
            this.overlay.style.setProperty('--engraving-font-size', px);
            this.overlay.style.setProperty('--engraving-tracking', view.letterSpacing || '0.08em');
            this.overlay.style.setProperty('--engraving-letter-spacing', view.letterSpacing || '0.08em');
            const rotVal = typeof view.rotation === 'string' ? view.rotation : `${parseInt(view.rotation||0,10)}deg`;
            this.overlay.style.setProperty('--engraving-rotate', rotVal);
            this.overlay.style.setProperty('--engraving-rotation', rotVal);
            this.overlay.style.setProperty('--engraving-color', view.color || '#262626');
            this.overlay.style.setProperty('opacity', String(view.opacity ?? 1));

            // NEW: alignment handling (left / center / right)
            const align = view.align || 'center';
            let translateX = '-50%';
            let originX    = '50%';
            let textAlign  = 'center';

            if (align === 'left') {
              translateX = '0%';
              originX    = '0%';
              textAlign  = 'left';
            } else if (align === 'right') {
              translateX = '-100%';
              originX    = '100%';
              textAlign  = 'right';
            }

            this.overlay.style.setProperty('--engraving-translate-x', translateX);
            this.overlay.style.setProperty('--engraving-origin-x', originX);
            this.overlay.style.setProperty('--engraving-text-align', textAlign);
            this.overlay.style.textAlign = textAlign;
          }
        }

        const note = (view && view.imageNote && view.imageNote.trim().length) ? view.imageNote : this.defaultImageNote;
        if (this.imageNoteEls && this.imageNoteEls.length) {
          this.imageNoteEls.forEach(el => { el.textContent = note; });
        }

        let nextMin = (view && Number.isFinite(view.min)) ? view.min : (fromInit ? this.min : this.min);
        let nextMax = (view && Number.isFinite(view.max)) ? view.max : (fromInit ? this.max : this.max);
        if (!Number.isFinite(nextMin) || nextMin < 1) nextMin = 1;
        if (!Number.isFinite(nextMax) || nextMax < nextMin) nextMax = nextMin;

        const nextAllowed = view && typeof view.allowed === 'string' ? view.allowed : (this.allowedChars ? this.allowedChars.join('') : '');

        this.min = nextMin;
        this.max = nextMax;
        this.allowedChars = (nextAllowed && nextAllowed.length) ? Array.from(nextAllowed.toUpperCase()) : this.allowedChars;

        if (this.input) {
          this.input.maxLength = this.max;
        }
        if (this.maxLabel) {
          this.maxLabel.textContent = (this.min === this.max)
            ? `2. Enter Text (${this.max} characters)`
            : `2. Enter Text (${this.min}-${this.max} characters)`;
        }
        if (this.allowedWrap && this.allowedTextEl) {
          if (this.allowedChars && this.allowedChars.length) {
            this.allowedTextEl.textContent = this.allowedChars.join('');
            this.allowedWrap.hidden = false;
          } else {
            this.allowedTextEl.textContent = '';
            this.allowedWrap.hidden = true;
          }
        }

        const hasHelp = !!(view && view.questionContent && view.questionContent.trim().length);
        if (this.qIcon) this.qIcon.style.display = hasHelp ? '' : 'none';
        if (!hasHelp && this.qContent) this.qContent.textContent = '';

        this.updateOverlay(this.input ? this.input.value : '');
        this.updateLocActiveUI();
      }

      setSubmitEnabled() {
        const enabled = this.canSubmit();
        if (!this.submitBtn) return;
        if (enabled) this.submitBtn.removeAttribute('disabled');
        else this.submitBtn.setAttribute('disabled', 'disabled');
      }

      setSpinner(active) {
        if (!this.submitBtn) return;
        const spinner = this.submitBtn.querySelector('.spinner');
        if (active) {
          this.submitBtn.setAttribute('aria-busy', 'true');
          if (spinner) spinner.classList.remove('hide');
        } else {
          this.submitBtn.removeAttribute('aria-busy');
          if (spinner) spinner.classList.add('hide');
          this.setSubmitEnabled();
        }
      }

      bindEvents() {
        if (this.input) {
          this.input.addEventListener('input', (e) => this.updateOverlay(e.target.value));
        }

        if (this.fontBtns.length) {
          this.fontBtns.forEach((btn) => {
            btn.addEventListener('click', () => {
              this.fontBtns.forEach(b => {
                const on = b === btn;
                b.classList.toggle('is-active', on);
                b.classList.toggle('card-swatches__button--active', on);
                b.setAttribute('aria-checked', on ? 'true' : 'false');
              });
              const label  = btn.dataset.fontLabel || btn.textContent.trim() || this.defaultFont;
              const stack  = btn.dataset.fontStack || `'${label}'`;
              const weight = btn.dataset.fontWeight || '700';
              this.applyFont(label, stack, weight);
            });
          });
        }

        if (this.locsRoot) {
          this.locsRoot.addEventListener('click', (e) => {
            const btn = e.target.closest('[data-view-key],[data-loc-key]');
            if (!btn || !this.locsRoot.contains(btn)) return;
            const key = btn.getAttribute('data-view-key') || btn.getAttribute('data-loc-key');
            if (key && key !== this.currentViewKey) this.applyView(key, false);
          });
        }

        if (this.consentEl) {
          this.consentEl.addEventListener('change', () => {
            this.showConsentError(!this.consentEl.checked);
            this.setSubmitEnabled();
          });
        }

        if (this.qIcon) {
          this.qIcon.addEventListener('click', (e) => {
            e.preventDefault(); e.stopPropagation();
            const view = this.views.find(v => v.key === this.currentViewKey);
            const text = (view && view.questionContent) ? view.questionContent : '';
            if (!text || !this.qWidget) return;
            if (this.qContent) this.qContent.textContent = text;
            this.portalQuestionWidget(true);
            this.blockDrawerOverlay(true);
            this.addModalDocCapture();
            this.qWidget.classList.add('open');
            this.qWidget.setAttribute('aria-hidden', 'false');
          });
        }

        document.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') this.closeQuestion();
        });

        if (this.submitBtn) {
          this.submitBtn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            if (e.currentTarget.closest('engraving-drawer-form') !== this) return;

            if (!this.canSubmit()) {
              this.showConsentError(!!this.consentEl && !this.consentEl.checked);
              try { this.consentEl && this.consentEl.focus({ preventScroll: true }); } catch (_){}
              return;
            }
            if (this._submitting) return;
            this._submitting = true;
            this.handleAddToCart().catch(console.error).finally(() => { this._submitting = false; });
          });
        }
      }

      closeQuestion() {
        if (!this.qWidget || !this.qWidget.classList.contains('open')) return;
        this.qWidget.classList.remove('open');
        this.qWidget.setAttribute('aria-hidden', 'true');
        this.removeModalDocCapture();
        this.blockDrawerOverlay(false);
        this.portalQuestionWidget(false);
      }

      canSubmit() {
        if (!this.input) return false;

        const val = (this.input.value || '').trim();
        const len = val.length;
        const okLen = len >= this.min && len <= this.max;
        const consentOk = this.consentEl ? !!this.consentEl.checked : true;

        return okLen && consentOk;
      }

      showConsentError(show) {
        const el = this.consentMsg;
        if (!el) return;
        if (show) {
          el.hidden = false;
          el.removeAttribute('hidden');
          el.classList.remove('hidden');
          el.style.display = 'block';
          el.setAttribute('role', 'alert');
          el.setAttribute('aria-live', 'polite');
        } else {
          el.hidden = true;
          el.setAttribute('hidden', '');
          el.classList.add('hidden');
          el.style.display = '';
          el.removeAttribute('role');
          el.removeAttribute('aria-live');
        }
      }

      getMainProductForm() {
        const formId = this.drawerEl && this.drawerEl.dataset ? this.drawerEl.dataset.mainProductFormId : '';
        if (formId) {
          const form = document.getElementById(formId);
          if (form) return form;
        }
        return null;
      }

      getRequestedQuantity() {
        const mainForm = this.getMainProductForm();
        let qtyInput = null;

        if (mainForm) {
          qtyInput = mainForm.querySelector('input[name="quantity"]');
        }

        if (!qtyInput && this.drawerEl && this.drawerEl.dataset && this.drawerEl.dataset.mainProductFormId) {
          qtyInput = document.querySelector(`input[name="quantity"][form="${this.drawerEl.dataset.mainProductFormId}"]`);
        }

        const qty = qtyInput ? parseInt(qtyInput.value, 10) : NaN;
        return Number.isFinite(qty) && qty > 0 ? qty : 1;
      }

      getMainSectionId() {
        const formId = this.drawerEl && this.drawerEl.dataset ? this.drawerEl.dataset.mainProductFormId : '';
        if (!formId) return null;

        const match = String(formId).match(/^product-form-(.+)$/);
        return match ? match[1] : null;
      }

      getVariantInventoryInfo(variantId) {
        if (!variantId) return null;

        const sectionId = this.getMainSectionId();
        let baseVariant = null;
        let inventoryVariant = null;

        if (sectionId) {
          const baseTag = document.querySelector(`#ProductVariantData-${CSS.escape(sectionId)}[type="application/json"]`);
          if (baseTag) {
            try {
              const baseData = JSON.parse(baseTag.textContent || '[]');
              if (Array.isArray(baseData)) {
                baseVariant = baseData.find(v => String(v.id) === String(variantId)) || null;
              }
            } catch (_e) {}
          }

          const inventoryTag = document.querySelector(`#ProductVariantData-${CSS.escape(sectionId)}-inventory[type="application/json"]`);
          if (inventoryTag) {
            try {
              const inventoryData = JSON.parse(inventoryTag.textContent || '[]');
              if (Array.isArray(inventoryData)) {
                inventoryVariant = inventoryData.find(v => String(v.id) === String(variantId)) || null;
              }
            } catch (_e) {}
          }
        }

        const inventoryManagement = baseVariant && Object.prototype.hasOwnProperty.call(baseVariant, 'inventory_management')
          ? baseVariant.inventory_management
          : null;

        const inventoryQuantityRaw =
          inventoryVariant && Object.prototype.hasOwnProperty.call(inventoryVariant, 'inventory_quantity')
            ? inventoryVariant.inventory_quantity
            : (baseVariant && Object.prototype.hasOwnProperty.call(baseVariant, 'inventory_quantity')
                ? baseVariant.inventory_quantity
                : null);

        const parsedQty = parseInt(inventoryQuantityRaw, 10);
        const inventoryQuantity = Number.isFinite(parsedQty) ? parsedQty : null;

        const inventoryPolicy =
          inventoryVariant && Object.prototype.hasOwnProperty.call(inventoryVariant, 'inventory_policy')
            ? inventoryVariant.inventory_policy
            : (baseVariant && Object.prototype.hasOwnProperty.call(baseVariant, 'inventory_policy')
                ? baseVariant.inventory_policy
                : null);

        return {
          inventoryManagement,
          inventoryQuantity,
          inventoryPolicy
        };
      }

      async getVariantQtyAlreadyInCart(variantId) {
        if (!variantId) return 0;

        try {
          const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
          const res = await fetch(root + 'cart.js', {
            credentials: 'same-origin',
            headers: { 'Accept': 'application/json' }
          });

          if (!res.ok) return 0;

          const cart = await res.json();
          const items = Array.isArray(cart && cart.items) ? cart.items : [];

          return items.reduce((sum, item) => {
            const itemVariantId = item && (item.variant_id || item.id);
            if (String(itemVariantId) === String(variantId)) {
              return sum + (parseInt(item.quantity, 10) || 0);
            }
            return sum;
          }, 0);
        } catch (_e) {
          return 0;
        }
      }

      async getRemainingEngravableQuantity(variantId) {
        const info = this.getVariantInventoryInfo(variantId);
        if (!info) return null;

        if (!info.inventoryManagement) return null;
        if (!Number.isFinite(info.inventoryQuantity)) return null;

        const inCartQty = await this.getVariantQtyAlreadyInCart(variantId);
        return Math.max(0, info.inventoryQuantity - inCartQty);
      }

      async canEngraveRequestedQuantity(variantId, requestedQty) {
        const remaining = await this.getRemainingEngravableQuantity(variantId);
        if (remaining === null) {
          return { ok: true, remaining: null };
        }

        return {
          ok: requestedQty <= remaining,
          remaining
        };
      }

      showFormMessage(message) {
        const msgWrap = this.querySelector('.form-message');
        const msgText = msgWrap && msgWrap.querySelector('.form-message__text');

        if (msgWrap && msgText) {
          msgText.textContent = message;
          msgWrap.classList.remove('hidden');
          return;
        }

        window.alert(message);
      }

      async handleAddToCart() {
        if (!this.submitBtn) return;

        if (!this.input) {
          console.warn('[engraving] Missing engraving input — blocking submission');
          this.showFormMessage('Please enter text to personalize your item.');
          this.setSubmitEnabled();
          return;
        }

        const text = (this.input.value || '').trim();

        if (!text) {
          console.warn('[engraving] Missing engraving text — blocking submission');
          this.showFormMessage('Please enter text to personalize your item.');
          this.setSubmitEnabled();
          return;
        }

        if (!this.addOnId) {
          console.warn('[engraving] Missing engraving add-on variant — blocking submission');
          this.showFormMessage('Sorry—engraving is not configured correctly for this product.');
          this.setSubmitEnabled();
          return;
        }

        const font = this.currentFont || this.defaultFont;
        const activeView = this.views.find(v => v.key === this.currentViewKey) || null;
        const loc = activeView ? (activeView.label || 'Front') : 'Front';
        const group = 'engr-' + Math.random().toString(36).slice(2, 10);

        let variantId = null;

        const mainForm = this.getMainProductForm();
        if (mainForm) {
          const radio = mainForm.querySelector('input[name="id"][type="radio"]:checked');
          const select = mainForm.querySelector('select[name="id"]');
          const hidden = mainForm.querySelector('input[name="id"][type="hidden"]');

          const src =
            (radio && radio.value) ||
            (select && select.value) ||
            (hidden && hidden.value);

          if (src) {
            const parsed = parseInt(src, 10);
            if (!Number.isNaN(parsed)) variantId = parsed;
          }
        }

        if (!variantId && this.productFormEl) {
          const variantInput = this.productFormEl.querySelector('input[name="id"]');
          if (variantInput && variantInput.value) {
            const parsed = parseInt(variantInput.value, 10);
            if (!Number.isNaN(parsed)) variantId = parsed;
          }
        }

        if (!variantId) {
          console.warn('[engraving] No variant id found for add-to-cart');
          this.showFormMessage('Sorry—we could not identify the selected product variant.');
          this.setSubmitEnabled();
          return;
        }

        const requestedQty = this.getRequestedQuantity();
        const qtyGuard = await this.canEngraveRequestedQuantity(variantId, requestedQty);

        if (!qtyGuard.ok) {
          const remaining = qtyGuard.remaining;

          if (remaining === 0) {
            this.showFormMessage('You already have the full available stock of this variant in your cart, no more can be engraved right now.');
          } else {
            this.showFormMessage(`Only ${remaining} more item${remaining === 1 ? '' : 's'} of this variant is currently available for engraving.`);
          }

          this.setSubmitEnabled();
          return;
        }

        this.setSpinner(true);
        this.submitBtn.setAttribute('disabled', 'disabled');

        try {
          const mainProps = {
            Personalization: 'Laser Engraving',
            'Engraving Text': text,
            'Engraving Font': font,
            'Engraving Location': loc,
            '_Engraving Info': `${text} | ${font} | ${loc}`,
            'Engraving Group': group,
            _LPROP: 'Final Sale'
          };

          if (this.confirmStatement) {
            mainProps['Terms'] = this.confirmStatement;
          }

          await this.postAddItems([
            {
              id: variantId,
              quantity: requestedQty,
              properties: mainProps
            },
            {
              id: parseInt(this.addOnId, 10),
              quantity: requestedQty,
              properties: {
                'Product': this.parentTitle || this.getParentTitleFallback(),
                'Engraving Group': group,
                '_LPROP': 'Final Sale'
              }
            }
          ]);

          const verified = await this.verifyEngravingGroup(group, requestedQty);

          if (!verified.ok) {
            console.warn('[engraving] Engraving group verification failed — cleaning up', verified);
            await this.removeEngravingGroup(group);
            throw new Error('Engraving group verification failed');
          }

          this.closeEngravingDrawer();
          await this.refreshViaTheme();

        } catch (err) {
          console.error('Engraving add failed:', err);

          try {
            await this.removeEngravingGroup(group);
          } catch (cleanupErr) {
            console.error('[engraving] Cleanup after failed add also failed:', cleanupErr);
          }

          this.showFormMessage('Sorry—something went wrong adding your personalization. Please try again.');
        } finally {
          this.setSpinner(false);
          this.setSubmitEnabled();
        }
      }

      async postAddItems(items) {
        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

        const res = await fetch(root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items }),
          credentials: 'same-origin'
        });

        if (!res.ok) {
          let info = '';
          try { info = await res.text(); } catch (_) {}
          throw new Error('cart/add.js multi-item failed: ' + res.status + ' ' + info);
        }

        return res.json();
      }

      async postAddOne(payload) {
        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        const res = await fetch(root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify(payload),
          credentials: 'same-origin'
        });

        if (!res.ok) {
          let info = '';
          try { info = await res.text(); } catch (_) {}
          throw new Error('cart/add.js failed: ' + res.status + ' ' + info);
        }

        return res.json();
      }

      async fetchCurrentCart() {
        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        const res = await fetch(root + 'cart.js', {
          credentials: 'same-origin',
          headers: { 'Accept': 'application/json' }
        });

        if (!res.ok) {
          throw new Error('cart.js failed: ' + res.status);
        }

        return res.json();
      }

      async verifyEngravingGroup(group, expectedQty) {
        const cart = await this.fetchCurrentCart();
        const items = Array.isArray(cart && cart.items) ? cart.items : [];

        const groupItems = items.filter((item) => {
          const props = item && item.properties ? item.properties : {};
          return props['Engraving Group'] === group;
        });

        const parent = groupItems.find((item) => {
          const props = item && item.properties ? item.properties : {};
          return props['Personalization'] && props['Engraving Text'] && props['_Engraving Info'];
        });

        const addon = groupItems.find((item) => {
          const props = item && item.properties ? item.properties : {};
          return props['Product'];
        });

        return {
          ok: !!parent && !!addon && parent.quantity >= expectedQty && addon.quantity >= expectedQty,
          parent: !!parent,
          addon: !!addon,
          parentQty: parent ? parent.quantity : 0,
          addonQty: addon ? addon.quantity : 0
        };
      }

      async removeEngravingGroup(group) {
        if (!group) return;

        const cart = await this.fetchCurrentCart();
        const items = Array.isArray(cart && cart.items) ? cart.items : [];

        const updates = {};

        items.forEach((item) => {
          const props = item && item.properties ? item.properties : {};
          if (props['Engraving Group'] === group && item.key) {
            updates[item.key] = 0;
          }
        });

        if (!Object.keys(updates).length) return;

        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        const res = await fetch(root + 'cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ updates })
        });

        if (!res.ok) {
          throw new Error('cart/update.js cleanup failed: ' + res.status);
        }

        return res.json();
      }

      async refreshViaTheme() {
        const loess = document.querySelector('loess-cart-drawer-items');
        if (loess && typeof loess.refreshCart === 'function') {
          await loess.refreshCart();
          return;
        }
        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        window.location.href = root + 'cart';
      }

      closeEngravingDrawer() {
        if (this.drawerCloseBtn) { this.drawerCloseBtn.click(); return; }
        if (this.drawerEl) this.drawerEl.removeAttribute('open');
      }
    }

    customElements.define('engraving-drawer-form', EngravingDrawerForm);
  }

  function patchOverlayForDrawer(drawer) {
    if (!drawer || drawer.dataset.overlayPatched === '1') return;
    drawer.dataset.overlayPatched = '1';

    const content  = drawer.querySelector('.drawer__inner') || drawer;
    const overlays = () => Array.from(document.querySelectorAll('.drawer__overlay,[close].drawer__overlay,#CartDrawer-Overlay'));

    let startedInside = false;
    let dragged       = false;
    let sx = 0, sy = 0;
    const THRESH = 8;
    let suppressOverlayClickUntil = 0;

    content.addEventListener('pointerdown', (e) => {
      if (e.button !== 0) return;
      startedInside = true;
      dragged = false;
      sx = e.clientX;
      sy = e.clientY;
    }, true);

    document.addEventListener('pointermove', (e) => {
      if (!startedInside || dragged) return;
      if (Math.abs(e.clientX - sx) > THRESH || Math.abs(e.clientY - sy) > THRESH) {
        dragged = true;
        overlays().forEach(ov => { ov.style.pointerEvents = 'none'; });
      }
    }, { capture: true, passive: true });

    const end = () => {
      if (!startedInside) return;
      if (dragged) suppressOverlayClickUntil = Date.now() + 300;
      setTimeout(() => overlays().forEach(ov => { ov.style.pointerEvents = ''; }), 50);
      startedInside = false;
      dragged = false;
    };
    document.addEventListener('pointerup', end, true);
    document.addEventListener('pointercancel', end, true);

    document.addEventListener('click', (e) => {
      const ov = e.target.closest('.drawer__overlay,[close].drawer__overlay,#CartDrawer-Overlay');
      if (!ov) return;

      // If ANY engraving drawer is open, never allow the overlay click to close it.
      // This affects all overlays only while an engraving drawer is open.
      const engravingOpen = document.querySelector('loess-drawer.engraving-cart-drawer[open]');
      if (engravingOpen) {
        e.stopPropagation();
        e.preventDefault();
        return;
      }

      // Otherwise (no engraving drawer open), keep the original "drag suppression" logic
      // so other drawers behave exactly as before.
      const now = Date.now();
      if (now > suppressOverlayClickUntil) return;

      e.stopPropagation();
      e.preventDefault();
    }, true);

  }

  function patchAllEngravingDrawers() {
    document.querySelectorAll('loess-drawer.engraving-cart-drawer').forEach(patchOverlayForDrawer);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', patchAllEngravingDrawers);
  } else {
    patchAllEngravingDrawers();
  }

  const _mo = new MutationObserver((muts) => {
    for (const m of muts) {
      for (const n of (m.addedNodes || [])) {
        if (!(n instanceof HTMLElement)) continue;
        if (n.matches && n.matches('loess-drawer.engraving-cart-drawer')) {
          patchOverlayForDrawer(n);
        } else if (n.querySelector) {
          const inner = n.querySelector('loess-drawer.engraving-cart-drawer');
          if (inner) patchOverlayForDrawer(inner);
        }
      }
    }
  });
  _mo.observe(document.documentElement || document.body, { childList: true, subtree: true });
})();
